"use client";

import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import {
  ArrowLeft,
  Calendar,
  Target,
  Shield,
  CheckCircle,
  Award,
  Users,
  Clock,
  GraduationCap,
  Ship,
  Download,
} from "lucide-react";
import { cadetshipDetails, cadetshipPhases } from "../data/courses";

const benefits = [
  {
    title: "Comprehensive Training",
    description: "Combined college education and practical on-ship experience",
    icon: GraduationCap,
  },
  {
    title: "Paid Training",
    description: "Earn stipend while gaining valuable maritime experience",
    icon: Award,
  },
  {
    title: "Career Guarantee",
    description: "Direct pathway to maritime officer positions",
    icon: Target,
  },
  {
    title: "Industry Recognition",
    description: "Globally recognized certification and training standards",
    icon: Award,
  },
];

const programHighlights = [
  {
    title: "Theoretical Foundation",
    description:
      "Comprehensive classroom education covering maritime theory, navigation, and safety protocols",
    icon: Users,
  },
  {
    title: "Practical Experience",
    description:
      "Hands-on training aboard commercial vessels with experienced officers",
    icon: Ship,
  },
  {
    title: "Expert Guidance",
    description:
      "Structured support and mentoring throughout the entire training program",
    icon: CheckCircle,
  },
  {
    title: "Career Support",
    description:
      "Job placement assistance and ongoing career guidance throughout the program",
    icon: Shield,
  },
];

const CadetshipTraining = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  const handleDownloadBrochure = () => {
    window.open("/cadetship-program-brochure.pdf", "_blank");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-teal-50/20">
      {/* Header */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-teal-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('/maritime-cadetship-training-program.png')] bg-cover bg-center"></div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-transparent"></div>

        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link
              to="/courses"
              className="mb-6 inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-blue-200 backdrop-blur-sm transition-all duration-300 hover:bg-white/20 hover:text-white"
            >
              <ArrowLeft size={20} className="mr-2" />
              Back to Categories
            </Link>
            <br />

            <h1 className="mb-4 text-4xl font-bold text-white sm:text-5xl lg:text-6xl">
              Cadetship Training
            </h1>
            <p className="max-w-3xl text-xl leading-relaxed text-slate-300">
              Comprehensive maritime cadetship program combining college
              education and on-ship training with stipend
            </p>
          </motion.div>
        </div>
      </section>

      {/* Course Overview */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center"
          >
            <h2 className="mb-4 text-3xl font-bold text-slate-900 sm:text-4xl">
              Program Overview
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-slate-600">
              A comprehensive maritime training program designed to prepare you
              for a successful career at sea
            </p>
          </motion.div>

          <div className="grid gap-8 lg:grid-cols-2">
            {/* Course Details Card */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="rounded-2xl border border-slate-200 bg-white p-8 shadow-lg"
            >
              <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 text-white">
                <GraduationCap className="h-6 w-6" />
              </div>
              <h3 className="mb-6 text-2xl font-bold text-slate-900">
                Course Details
              </h3>

              <div className="space-y-4">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <div className="flex items-center text-slate-600">
                    <Clock className="mr-2 h-4 w-4" />
                    Duration
                  </div>
                  <span className="font-semibold text-slate-900">
                    {cadetshipDetails.duration}
                  </span>
                </div>

                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <div className="flex items-center text-slate-600">
                    <Ship className="mr-2 h-4 w-4" />
                    Training Type
                  </div>
                  <span className="font-semibold text-slate-900">
                    {cadetshipDetails.type}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center text-slate-600">
                    <Target className="mr-2 h-4 w-4" />
                    Stipend During Training
                  </div>
                  <span className="font-semibold text-green-600">
                    Available during on-ship phase
                  </span>
                </div>
              </div>

              <div className="mt-8 border-t border-slate-200 pt-8">
                <h4 className="mb-6 text-xl font-bold text-slate-900">
                  Program Phases
                </h4>
                <div className="space-y-4">
                  {cadetshipPhases.map((phase, index) => (
                    <motion.div
                      key={phase.phase}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                      className="flex items-center gap-4 rounded-xl bg-gradient-to-r from-slate-50 to-blue-50 p-4"
                    >
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-teal-500 to-teal-600 text-white">
                        <span className="font-bold">{phase.phase}</span>
                      </div>
                      <div className="flex-1">
                        <h5 className="font-semibold text-slate-900">
                          {phase.course}
                        </h5>
                        <p className="text-sm font-medium text-teal-600">
                          {phase.duration}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Program Information Card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="rounded-2xl border border-slate-200 bg-white p-8 shadow-lg"
            >
              <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <Calendar className="h-6 w-6" />
              </div>
              <h3 className="mb-6 text-2xl font-bold text-slate-900">
                Program Information
              </h3>

              <div className="space-y-4">
                <div className="rounded-lg bg-slate-50 p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">
                    Program Structure
                  </h4>
                  <p className="text-sm text-slate-600">
                    The cadetship program combines theoretical classroom
                    learning with practical on-ship training, providing
                    comprehensive maritime education and professional
                    development.
                  </p>
                </div>

                <div className="rounded-lg bg-slate-50 p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">
                    Eligibility
                  </h4>
                  <p className="text-sm text-slate-600">
                    Candidates must meet specific educational and health
                    requirements. Contact our admissions team for detailed
                    eligibility criteria.
                  </p>
                </div>

                <div className="rounded-lg bg-slate-50 p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">
                    Career Outcomes
                  </h4>
                  <p className="text-sm text-slate-600">
                    Successful completion leads to globally recognized maritime
                    officer certifications with direct pathway to employment
                    opportunities.
                  </p>
                </div>

                <div className="rounded-lg bg-gradient-to-r from-teal-50 to-blue-50 p-4">
                  <p className="font-semibold text-slate-900">
                    Interested in learning more?
                  </p>
                  <p className="text-sm text-slate-600 mt-1">
                    For any queries, email us at{" "}
                    <a
                      href="mailto:training@nautilustimes.com"
                      className="font-semibold text-teal-700 underline"
                    >
                      training@nautilustimes.com
                    </a>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-12 flex flex-col justify-center gap-4 sm:flex-row"
          >
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-teal-600 to-teal-700 px-8 py-4 text-lg font-semibold text-white shadow-lg transition-all duration-300 hover:scale-105 hover:from-teal-700 hover:to-teal-800 hover:shadow-xl"
            >
              <CheckCircle className="mr-2 h-5 w-5" />
              Contact for Information
            </Link>
            <button
              onClick={handleDownloadBrochure}
              className="inline-flex items-center justify-center rounded-xl border-2 border-teal-600 bg-white px-8 py-4 text-lg font-semibold text-teal-600 shadow-md transition-all duration-300 hover:scale-105 hover:bg-teal-50 hover:shadow-lg"
            >
              <Download className="mr-2 h-5 w-5" />
              Download Brochure
            </button>
          </motion.div>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-3xl font-bold text-slate-900 sm:text-4xl">
              Program Highlights
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-slate-600">
              Comprehensive training designed to prepare you for a successful
              maritime career
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {programHighlights.map((highlight, index) => (
              <motion.div
                key={highlight.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group text-center"
              >
                <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500 to-teal-600 text-white shadow-lg transition-transform duration-300 group-hover:scale-110">
                  <highlight.icon className="h-8 w-8" />
                </div>
                <h3 className="mb-3 text-xl font-semibold text-slate-900">
                  {highlight.title}
                </h3>
                <p className="leading-relaxed text-slate-600">
                  {highlight.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="bg-slate-900 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-3xl font-bold text-white sm:text-4xl">
              Why Choose Our Cadetship Program?
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-slate-300">
              Industry-leading cadetship program with proven track record of
              success
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group text-center"
              >
                <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500 to-teal-600 text-white shadow-lg transition-transform duration-300 group-hover:scale-110">
                  <benefit.icon className="h-8 w-8" />
                </div>
                <h3 className="mb-3 text-xl font-semibold text-white">
                  {benefit.title}
                </h3>
                <p className="leading-relaxed text-slate-300">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CadetshipTraining;
